import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthStore, useTestStore } from '../store';
import { localApi, initializeLocalApi } from '../utils/local-api';
import { api } from '../utils/api';
import { TestSession } from '../types';
import { ChevronLeft, ChevronRight, BookOpen, AlertCircle, Clock } from 'lucide-react';
import PinyinText from '../components/PinyinText';
import PartCountConfig from '../components/PartCountConfig';
import { getParts, getDefaults, parseLevel, snapToGroup } from '../config/hskTestParts';
import { getSequenceComposerTokens, hasIndependentGroupOptions } from '../utils/simulationQuestionPresentation';
import { buildCorrectSequence } from '../utils/sequenceQuestionScoring';

const DONT_KNOW_VALUE = -1;

const ReadingTest: React.FC = () => {
  const navigate = useNavigate();
  const { level: levelParam } = useParams<{ level: string }>();
  const HSK_LEVEL = parseLevel(levelParam);
  const TEST_PART = 'reading';
  const TIME_LIMIT = 30 * 60; // 30 minutes in seconds
  const shouldShowPinyin = HSK_LEVEL === 2;
  const parts = getParts(HSK_LEVEL, 'reading');

  const { token } = useAuthStore();
  const { currentTest: storeCurrentTest, startTest, answerQuestion, nextQuestion, previousQuestion, completeTest, addTestToHistory, clearCurrentTest } = useTestStore();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [localApiAvailable, setLocalApiAvailable] = useState(true);
  const [initializingApi, setInitializingApi] = useState(true);
  const [isCloudflare, setIsCloudflare] = useState(false);
  const [cloudSessionId, setCloudSessionId] = useState<string | null>(null);
  const [timeRemaining, setTimeRemaining] = useState(TIME_LIMIT);
  const [testStartTime, setTestStartTime] = useState<Date | null>(null);
  const [completedSession, setCompletedSession] = useState<TestSession | null>(null);
  const [reviewIndex, setReviewIndex] = useState(0);
  const [groupSelections, setGroupSelections] = useState<number[]>([]);
  const [isCheckpointSaving, setIsCheckpointSaving] = useState(false);
  const [checkpointError, setCheckpointError] = useState('');
  const [showConfig, setShowConfig] = useState(false);
  const [readingConfig, setReadingConfig] = useState<Record<string, number>>(() => getDefaults(HSK_LEVEL, 'reading'));
  // Sequence (ordering) answers — only used for HSK 4 R3 sentence-ordering questions.
  const [writingAnswers, setWritingAnswers] = useState<Record<number, { sequence?: string[] }>>({});
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  const isSimulationMode = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('sim') === '1';
  const isReviewMode = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('review') === '1';
  const examScope = `hsk:2.0:simulation:${HSK_LEVEL}:${TEST_PART}:`;
  const currentTest = storeCurrentTest && (
    (isReviewMode && storeCurrentTest.testId?.startsWith('practice-')) || storeCurrentTest.examScope === examScope
  ) ? storeCurrentTest : null;

  useEffect(() => {
    setReadingConfig(getDefaults(HSK_LEVEL, 'reading'));
  }, [HSK_LEVEL]);

  const renderText = (text: string, className = '') =>
    shouldShowPinyin
      ? <PinyinText text={text} className={className} />
      : <span className={className}>{text}</span>;

  // --- Sequence (ordering) helpers (HSK 4 R3) ---
  const isSequenceQuestion = (q: any) =>
    Number(q?.level ?? HSK_LEVEL) === 4 && String(q?.part || '').toUpperCase() === 'R3';
  const getTokens = (question: any) => getSequenceComposerTokens(question, HSK_LEVEL);
  const getTokenCounts = (tokens: string[]) => {
    const counts: Record<string, number> = {};
    tokens.forEach(t => counts[t] = (counts[t] || 0) + 1);
    return counts;
  };
  const reconstructCorrectSeq = (q: any): string[] => {
	return buildCorrectSequence(q);
  };
  const selectToken = (t: string) => {
    const idx = currentTest?.currentQuestionIndex || 0;
    const cur = writingAnswers[idx]?.sequence || [];
    setWritingAnswers({ ...writingAnswers, [idx]: { sequence: [...cur, t] } });
  };
  const removeToken = (indexToRemove: number) => {
    const idx = currentTest?.currentQuestionIndex || 0;
    const cur = writingAnswers[idx]?.sequence || [];
    setWritingAnswers({ ...writingAnswers, [idx]: { sequence: cur.filter((_, i) => i !== indexToRemove) } });
  };
  const onDragStart = (e: React.DragEvent<HTMLDivElement>, i: number) => {
    setDragIndex(i);
    e.dataTransfer.effectAllowed = 'move';
  };
  const onDragOver = (e: React.DragEvent<HTMLDivElement>, overIndex: number) => {
    e.preventDefault();
    if (dragIndex === null) return;
    const idx = currentTest?.currentQuestionIndex || 0;
    const cur = writingAnswers[idx]?.sequence || [];
    if (dragIndex === overIndex) return;
    const newSeq = [...cur];
    const [moved] = newSeq.splice(dragIndex, 1);
    newSeq.splice(overIndex, 0, moved);
    setWritingAnswers({ ...writingAnswers, [idx]: { sequence: newSeq } });
    setDragIndex(overIndex);
  };

  // Timer effect — only runs while an actual test is in progress (not on the
  // configuration screen or the results screen).
  useEffect(() => {
    if (!currentTest || showConfig) return;
    if (timeRemaining > 0 && !showResults) {
      const timer = setTimeout(() => setTimeRemaining(timeRemaining - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeRemaining === 0) {
      handleComplete();
    }
  }, [timeRemaining, showResults, currentTest, showConfig]);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Start (or resume) a reading test for the given per-part configuration.
  const loadAndStart = async (cfg: Record<string, number>) => {
    if (!token) return;

    const partCounts = parts
      .map(p => ({ part: p.key, count: snapToGroup(cfg[p.key] ?? 0, p.groupSize) }))
      .filter(item => item.count > 0);
    const totalQuestions = partCounts.reduce((sum, item) => sum + item.count, 0);
    if (totalQuestions <= 0) {
      setError('请至少设置 1 道阅读题。');
      return;
    }

    try {
      setError('');
      setShowConfig(false);
      setInitializingApi(true);
      setLoading(true);

      const host = typeof window !== 'undefined' ? window.location.host : '';
      const cf = /workers\.dev|pages\.dev/i.test(host);
      setIsCloudflare(cf);
      const subparts = partCounts.map(item => item.part);

      const mapQuestion = (q: any, hideAnswer: boolean) => ({
        id: q.question_id,
        level: q.level ?? HSK_LEVEL,
        part: q.part || TEST_PART,
        question: q.question_text,
        options: q.options,
        correctAnswer: hideAnswer ? -1 : q.correct_index,
        media: q.media,
        order: q.order,
		explanation: q.explanation,
        questionGroup: q.question_group ?? null,
      });

      const applyResume = (res: any, questions: any[]) => {
        let initialIndex = 0;
        let initialAnswers: (number | null)[] | undefined = undefined;
        const waInit: Record<number, { sequence?: string[] }> = {};
        if (res.resumed && res.progress) {
          initialIndex = res.progress.index || 0;
          if (res.progress.answers) {
            initialAnswers = new Array(questions.length).fill(null);
            Object.entries(res.progress.answers).forEach(([idxStr, ans]) => {
              const idx = Number(idxStr);
              if (idx < 0 || idx >= questions.length) return;
              if (Array.isArray(ans)) waInit[idx] = { sequence: ans as string[] };
              else if (typeof ans === 'number') initialAnswers![idx] = ans;
            });
          }
        }
        setWritingAnswers(waInit);
        return { initialIndex, initialAnswers };
      };

      if (cf) {
        setLocalApiAvailable(true);
        const res = await api.startTest(HSK_LEVEL, TEST_PART, totalQuestions, token, partCounts);
        setCloudSessionId(res.test_session_id);
        const questions = res.questions.map((q: any) => mapQuestion(q, true));
        const { initialIndex, initialAnswers } = applyResume(res, questions);
        startTest(HSK_LEVEL, TEST_PART, questions, res.test_session_id, initialIndex, initialAnswers, examScope);
        setTestStartTime(new Date());
        setTimeRemaining(TIME_LIMIT);
        return;
      }

      const apiAvailable = await initializeLocalApi();
      setLocalApiAvailable(apiAvailable);
      if (!apiAvailable) {
        setError('Local API server is not running. Please start the local server first.');
        return;
      }

      const response = await localApi.startTest(HSK_LEVEL, TEST_PART, totalQuestions, token, subparts, partCounts);
      if (!response.success) {
        throw new Error('Failed to start test');
      }
      if (!response.questions || response.questions.length === 0) {
        throw new Error('题库暂无题目，请联系管理员添加阅读题目后再试。');
      }
      const questions = response.questions.map((q: any) => mapQuestion(q, false));
      const { initialIndex, initialAnswers } = applyResume(response, questions);
      startTest(HSK_LEVEL, TEST_PART, questions, response.test_id, initialIndex, initialAnswers, examScope);
      setTestStartTime(new Date());
      setTimeRemaining(TIME_LIMIT);
    } catch (err: any) {
      console.error('Failed to load Reading questions:', err);
      setError(err.message || 'Failed to load Reading questions');
    } finally {
      setLoading(false);
      setInitializingApi(false);
    }
  };

  useEffect(() => {
    if (!token) {
      navigate('/');
      return;
    }

    const prepare = async () => {
      setInitializingApi(true);
      setLoading(true);

      const isReviewMode = new URLSearchParams(window.location.search).get('review') === '1';
      const currentIsPracticeSession = currentTest?.testId?.startsWith('practice-');

      if (isReviewMode && currentIsPracticeSession) {
        setTestStartTime(new Date());
        setTimeRemaining(TIME_LIMIT);
        setLoading(false);
        setInitializingApi(false);
        return;
      }

      if (!isReviewMode && currentIsPracticeSession) {
        clearCurrentTest();
      }

      const isMatchingTest = !currentIsPracticeSession &&
                             currentTest &&
                             currentTest.questions &&
                             currentTest.questions.length > 0 &&
                             currentTest.part === TEST_PART &&
                             currentTest.level === HSK_LEVEL;

      if (isMatchingTest) {
        try {
          const activeRes = await localApi.getActiveTests(token);
          const isStillActive = (activeRes.active || []).some((t: any) => t.id === currentTest.testId);
          if (isStillActive) {
            setTestStartTime(new Date());
            setTimeRemaining(TIME_LIMIT);
            setLoading(false);
            setInitializingApi(false);
            return;
          }
        } catch {
          setTestStartTime(new Date());
          setTimeRemaining(TIME_LIMIT);
          setLoading(false);
          setInitializingApi(false);
          return;
        }
      }

      // Full Simulation enters here with ?sim=1 — keep the fixed exam structure.
      if (isSimulationMode) {
        await loadAndStart(getDefaults(HSK_LEVEL, 'reading'));
        return;
      }

      // Standalone practice: let the student choose how many questions per part.
      setShowConfig(true);
      setLoading(false);
      setInitializingApi(false);
    };

    prepare();
  }, [token, startTest, navigate, HSK_LEVEL]);

  useEffect(() => {
    if (currentTest && currentTest.questions && currentTest.answers) {
        const index = currentTest.currentQuestionIndex;
        if (currentTest.answers[index] !== undefined && currentTest.answers[index] !== null) {
            setSelectedAnswer(currentTest.answers[index]);
        } else {
            setSelectedAnswer(null);
        }

        // Auto-save progress (skipped for client-only review/practice sessions)
        if (currentTest.testId && token && !currentTest.testId.startsWith('practice-')) {
            const idx = currentTest.currentQuestionIndex;
            const answersObj: Record<string, any> = {};

            currentTest.answers.forEach((ans, i) => {
                if (ans !== null) answersObj[i] = ans;
            });
            Object.entries(writingAnswers).forEach(([k, v]) => {
                if (v.sequence && v.sequence.length) answersObj[k] = v.sequence;
            });

            localApi.saveProgress(
                currentTest.testId,
                idx,
                answersObj,
                token
            ).catch(e => console.warn("Failed to auto-save", e));
        }

        if (currentTest.testId?.startsWith('practice-')) {
            setGroupSelections([]);
            return;
        }

        // Peer indices sharing an option pool. Prefer questionGroup metadata
        // (works with any custom count); fall back to static 5-question ranges.
        const currentQ: any = currentTest.questions[index];
		if (hasIndependentGroupOptions(currentQ, HSK_LEVEL)) {
			setGroupSelections([]);
			return;
		}
        const currentGroup = currentQ?.questionGroup ?? null;
        let peerIndices: number[] = [];

        if (currentGroup) {
            peerIndices = currentTest.questions
                .map((q: any, i) => ({ q, i }))
                .filter(({ q, i }) => i !== index && (q?.questionGroup ?? null) === currentGroup)
                .map(({ i }) => i);
        } else {
            let groupStart = -1;
            let groupEnd = -1;
            if (index >= 0 && index < 5) { groupStart = 0; groupEnd = 4; }
            else if (index >= 5 && index < 10) { groupStart = 5; groupEnd = 9; }
            else if (index >= 10 && index < 15) { groupStart = 10; groupEnd = 14; }
            else if (index >= 15 && index < 20) { groupStart = 15; groupEnd = 19; }
            if (groupStart !== -1) {
                for (let i = groupStart; i <= groupEnd; i++) {
                    if (i !== index) peerIndices.push(i);
                }
            }
        }

        const usedOptions: number[] = [];
        peerIndices.forEach((i) => {
            const ans = currentTest.answers[i];
            if (ans !== undefined && ans !== null && ans !== DONT_KNOW_VALUE) {
                usedOptions.push(ans as number);
            }
        });
        setGroupSelections(usedOptions);
    }
  }, [currentTest?.currentQuestionIndex, currentTest?.answers, currentTest?.testId, token, isCloudflare, writingAnswers]);

  const handleAnswer = (answerIndex: number) => {
    setSelectedAnswer(answerIndex);
    answerQuestion(answerIndex);
  };

  const handleNext = async () => {
    setSelectedAnswer(null);
    if (isCheckpointSaving) return;

    if (currentTest && currentTest.currentQuestionIndex < currentTest.questions.length - 1) {
      const isPracticeSession = currentTest.testId?.startsWith('practice-');
      if ((currentTest.currentQuestionIndex + 1) % 10 === 0 && currentTest.testId && token && !isPracticeSession) {
        setIsCheckpointSaving(true);
        setCheckpointError('');

        try {
          const idx = currentTest.currentQuestionIndex;
          const answersObj: Record<string, any> = {};
          currentTest.answers.forEach((ans, i) => { if (ans !== null) answersObj[i] = ans; });
          Object.entries(writingAnswers).forEach(([k, v]) => {
            if (v.sequence && v.sequence.length) answersObj[k] = v.sequence;
          });
          await localApi.saveProgress(currentTest.testId, idx, answersObj, token);
        } catch (err) {
          console.error("Checkpoint save failed:", err);
          setCheckpointError('网络连接异常，进度保存失败，请检查网络后重试。');
        } finally {
          setIsCheckpointSaving(false);
          nextQuestion();
        }
      } else {
        nextQuestion();
      }
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    setSelectedAnswer(null);
    previousQuestion();
  };

  const handleComplete = async () => {
    if (!currentTest) return;

    const isPracticeSession = currentTest.testId?.startsWith('practice-');
    const session = completeTest();
    if (session && isPracticeSession) {
      const reviewed = { ...session, answers: session.questions.map((q, index) => isSequenceQuestion(q) ? { sequence: writingAnswers[index]?.sequence || [] } : session.answers[index]) };
      addTestToHistory({ ...reviewed, score: 0 });
      setCompletedSession(reviewed);
      setShowResults(true);
      return;
    }
    if (session && token && localApiAvailable) {
      try {
        const answers = session.questions.map((q, index) => {
          if (isSequenceQuestion(q)) {
            const seq = writingAnswers[index]?.sequence || [];
            return {
              question_id: q.id,
              chosen_option_index: null,
              chose_buzhidao: false,
              time_spent_seconds: 30,
              chosen_sequence: seq.length > 0 ? seq : null,
            };
          }
          const answer = session.answers[index];
          return {
            question_id: q.id,
            chosen_option_index: answer === DONT_KNOW_VALUE ? null : answer,
            chose_buzhidao: answer === DONT_KNOW_VALUE,
            time_spent_seconds: testStartTime ? Math.floor((Date.now() - testStartTime.getTime()) / 1000) : 30,
          };
        });

        if (isCloudflare && cloudSessionId) {
            await api.submitTest(cloudSessionId, answers as any, token);
            addTestToHistory(session);
        } else {
            const response = await localApi.submitTest(session.id, answers as any, token);
            if (response.success) {
              addTestToHistory(session);
            } else {
              throw new Error(response.message || 'Failed to submit test');
            }
        }
      } catch (err: any) {
        console.error('Failed to submit Reading test:', err);
        setError(err.message || 'Failed to submit Reading test');
        try {
           const history = await localApi.getHistory(token);
           if (history.some((h: any) => h.id === session.id)) {
               setError('');
               addTestToHistory(session);
           }
        } catch (e) {
            // Ignore history fetch error
        }
      } finally {
        setCompletedSession(session);
        const params = new URLSearchParams(window.location.search);
        if (isCloudflare) {
           navigate('/history');
        } else if (params.get('sim') === '1') {
          navigate(`/test/hsk${HSK_LEVEL}/writing?sim=1`);
        } else {
          setShowResults(true);
        }
      }
    }
  };

  if (loading || initializingApi) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">
            {initializingApi ? 'Initializing...' : 'Loading Reading questions...'}
          </p>
        </div>
      </div>
    );
  }

  if (error && !showResults && !showConfig) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
          <h3 className="font-semibold">Reading Test Error</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (showResults && completedSession) {
    const total = completedSession.questions.length;

    const questionCorrect = (i: number) => {
      const q = completedSession.questions[i];
      if (isSequenceQuestion(q)) {
        const correct = reconstructCorrectSeq(q);
        const user = (writingAnswers[i]?.sequence || []).map(s => String(s).trim());
        return correct.length === user.length && correct.every((t, k) => t === user[k]);
      }
      const a = completedSession.answers[i];
      return a !== null && a !== DONT_KNOW_VALUE && a === q.correctAnswer;
    };
    const questionUnknown = (i: number) => {
      const q = completedSession.questions[i];
      if (isSequenceQuestion(q)) return (writingAnswers[i]?.sequence || []).length === 0;
      return completedSession.answers[i] === DONT_KNOW_VALUE;
    };
    const questionWrong = (i: number) => !questionCorrect(i) && !questionUnknown(i) &&
      (isSequenceQuestion(completedSession.questions[i]) || completedSession.answers[i] !== null);

    const score = completedSession.questions.reduce((acc, _q, i) => acc + (questionCorrect(i) ? 1 : 0), 0);
    const isReviewSession = completedSession.id?.startsWith('practice-');

    const scrollToCard = (i: number) => {
      const el = document.getElementById(`rq-${i}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setReviewIndex(i);
    };

    return (
      <div className="max-w-6xl mx-auto">
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900">HSK {HSK_LEVEL}级 阅读测试 回顾</h2>
              <p className="text-sm text-gray-600">得分 {score}/{total}（{Math.round(((score || 0) / total) * 100)}%）</p>
            </div>
            <div className="space-x-2">
              <button onClick={() => navigate(isReviewSession ? '/history' : '/')} className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                {isReviewSession ? 'Back to History' : '返回主页'}
              </button>
              <button onClick={() => navigate('/history')} className="px-3 py-2 bg-gray-600 text-white rounded hover:bg-gray-700">查看历史</button>
            </div>
          </div>

          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-3 sticky top-4 h-[calc(100vh-2rem)] overflow-y-auto bg-white/90 backdrop-blur rounded-md p-2">
              <div className="grid grid-cols-5 gap-2">
                {completedSession.questions.map((_, i) => {
                  const btnCorrect = questionCorrect(i);
                  const btnBad = questionUnknown(i) || questionWrong(i);
                  const base = 'flex items-center justify-center h-10 rounded border text-sm font-medium';
                  const color = btnCorrect
                    ? 'border-green-500 bg-green-100 text-green-800'
                    : btnBad
                      ? 'border-red-500 bg-red-100 text-red-800'
                      : 'border-gray-300 bg-gray-100 text-gray-700';
                  const active = i === reviewIndex ? 'ring-2 ring-offset-1 ' + (btnCorrect ? 'ring-green-400' : 'ring-red-400') : '';
                  return (
                    <button key={i} onClick={() => scrollToCard(i)} className={`${base} ${color} ${active}`}>{i + 1}</button>
                  );
                })}
              </div>
            </div>

            <div className="col-span-9 space-y-6">
              {completedSession.questions.map((qItem, idx) => {
                const cardCorrect = questionCorrect(idx);
                const cardUnknown = questionUnknown(idx);
                const cardClass = cardCorrect ? 'border-green-500 ring-2 ring-green-400' : 'border-red-500 ring-2 ring-red-400';
                const seq = isSequenceQuestion(qItem);
                const ans = completedSession.answers[idx];
                return (
                  <div key={idx} id={`rq-${idx}`} className={`p-6 border rounded-lg ${cardClass}`}>
                    <div className="flex items-center justify-between mb-4">
                      <div className="text-sm text-gray-600">第 {idx + 1} 题</div>
                      <div className="text-sm">
                        {cardCorrect ? (
                          <span className="text-green-600 font-semibold">回答正确</span>
                        ) : cardUnknown ? (
                          <span className="text-red-600 font-semibold">未作答</span>
                        ) : (
                          <span className="text-red-600 font-semibold">回答错误</span>
                        )}
                      </div>
                    </div>

                    {seq ? (
                      <div className="space-y-3">
                        <div className="p-4 border rounded">
                          <div className="text-sm text-gray-600 mb-2">你的答案</div>
                          <div className="flex flex-wrap gap-2">
                            {(writingAnswers[idx]?.sequence || []).map((t, i) => (
                              <span key={t + i} className="px-2 py-1 border rounded bg-gray-50">{renderText(t)}</span>
                            ))}
                            {(writingAnswers[idx]?.sequence || []).length === 0 && (
                              <span className="text-gray-400">未作答</span>
                            )}
                          </div>
                        </div>
                        <div className="p-4 border rounded">
                          <div className="text-sm text-gray-600 mb-2">正确答案</div>
                          <div className="flex flex-wrap gap-2">
                            {reconstructCorrectSeq(qItem).map((t, i) => (
                              <span key={String(t) + i} className="px-2 py-1 border rounded bg-green-50">{renderText(t)}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="mb-4 text-lg font-medium text-gray-900 whitespace-pre-line">{renderText(qItem.question.replace(/★/g, '\n★'))}</div>
                        <div className="space-y-3">
                          {qItem.options.map((opt, oidx) => {
                            const isChosen = ans === oidx;
                            const isRight = qItem.correctAnswer === oidx;
                            const optClass = isRight
                              ? 'border-green-500 bg-green-50'
                              : isChosen
                                ? 'border-red-500 bg-red-50'
                                : 'border-gray-300';
                            return (
                              <div key={oidx} className={`p-4 border rounded`}>
                                <div className={`flex items-center ${optClass}`}>
                                  <span className={`font-medium mr-3 ${isRight ? 'text-green-600' : isChosen ? 'text-red-600' : 'text-gray-600'}`}>{String.fromCharCode(65 + oidx)}.</span>
                                  {renderText(opt, "text-gray-800")}
                                </div>
                              </div>
                            );
                          })}
                          {cardUnknown && (
                            <div className="p-4 border border-red-500 rounded bg-red-50 text-red-700">选择了 不知道</div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (showConfig && !currentTest) {
    return (
      <PartCountConfig
        title="自定义阅读题量"
        subtitle="设置每个部分要练习的题目数量，然后开始阅读测试。"
        Icon={BookOpen}
        parts={parts}
        config={readingConfig}
        onChange={setReadingConfig}
        onStart={() => loadAndStart(readingConfig)}
        startLabel="开始阅读测试"
        error={error}
      />
    );
  }

  if (!currentTest || !currentTest.questions || currentTest.questions.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <p className="text-gray-600">No Reading test data available</p>
      </div>
    );
  }

  const currentQuestion = currentTest.questions[currentTest.currentQuestionIndex];
  if (!currentQuestion) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <p className="text-gray-600">Question not found</p>
      </div>
    );
  }

  const progress = ((currentTest.currentQuestionIndex + 1) / currentTest.questions.length) * 100;
  const seqMode = isSequenceQuestion(currentQuestion);
  const curIdx = currentTest.currentQuestionIndex;

  // Sequence composer data
  const seqTokens = seqMode ? getTokens(currentQuestion) : [];
  const usedSeq = writingAnswers[curIdx]?.sequence || [];
  const totalCounts = getTokenCounts(seqTokens);
  const usedCounts = getTokenCounts(usedSeq);
  const remainingTokens: string[] = [];
  Object.keys(totalCounts).forEach(t => {
    const remaining = totalCounts[t] - (usedCounts[t] || 0);
    for (let i = 0; i < remaining; i++) remainingTokens.push(t);
  });

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white shadow rounded-lg">
        {/* Header with branding and timer */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h1 className="text-xl font-semibold text-blue-900">
                HSK {HSK_LEVEL}级 阅读测试 (HSK {HSK_LEVEL} Reading Test)
              </h1>
              <p className="text-sm text-blue-700">汉语水平考试 - Chinese Proficiency Test</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center text-blue-600">
                <Clock className="w-4 h-4 mr-1" />
                <span className={timeRemaining < 300 ? 'text-red-600 font-semibold' : ''}>
                  {formatTime(timeRemaining)}
                </span>
              </div>
              <span className="text-sm text-blue-600 font-medium">
                Question {currentTest.currentQuestionIndex + 1} of {currentTest.questions.length}
              </span>
            </div>
          </div>
          <div className="w-full bg-blue-200 rounded-full h-3">
            <div
              className="bg-blue-600 h-3 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        <div className="p-6">
          {seqMode ? (
            <div className="mb-8">
              <div className="mb-3 text-sm text-gray-600">点击词语按正确顺序排列，可拖动调整顺序。</div>
              <div className="space-y-3 mb-4">
                {remainingTokens.map((t, i) => (
                  <button
                    key={t + i}
                    onClick={() => selectToken(t)}
                    className="block w-full rounded border bg-gray-50 px-4 py-3 text-left hover:bg-gray-100 transition-colors"
                  >
                    {renderText(t)}
                  </button>
                ))}
              </div>
              <div className="p-3 border rounded min-h-16 bg-gray-50">
                <div className="space-y-2">
                  {usedSeq.map((t, i) => (
                    <div
                      key={t + i}
                      draggable
                      onDragStart={(e) => onDragStart(e, i)}
                      onDragOver={(e) => onDragOver(e, i)}
                      className="px-3 py-2 border rounded bg-blue-100 cursor-move hover:bg-blue-200 transition-colors flex items-center justify-between"
                    >
                      <span>{renderText(t)}</span>
                      <button onClick={(e) => { e.stopPropagation(); removeToken(i); }} className="ml-2 text-blue-400 hover:text-blue-600">×</button>
                    </div>
                  ))}
                  {usedSeq.length === 0 && (
                    <span className="text-gray-400 italic">点击上方词语作答...</span>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="mb-8">
              <h2 className="text-lg font-medium text-gray-900 mb-4 whitespace-pre-line">
                {renderText(currentQuestion.question.replace(/★/g, '\n★'))}
              </h2>

              <div className="space-y-3">
                {currentQuestion.options.map((option, index) => {
                    const isUsedInGroup = groupSelections.includes(index);
                    return (
                  <button
                    key={index}
                    onClick={() => handleAnswer(index)}
                    className={`w-full text-left p-4 border rounded-lg transition-colors ${
                      selectedAnswer === index
                        ? 'border-blue-500 bg-blue-50 shadow-md'
                        : isUsedInGroup
                          ? 'border-red-500 bg-gray-50'
                          : 'border-gray-300 hover:border-blue-400 hover:bg-blue-25'
                    }`}
                  >
                    <div className="relative w-full">
                      <span className="font-medium mr-3 text-blue-600">{String.fromCharCode(65 + index)}.</span>
                      {renderText(option, "text-gray-800")}
                      {isUsedInGroup && (
                          <span className="absolute top-0 right-0 bg-red-100 text-red-600 text-xs px-2 py-1 rounded-full border border-red-200">
                              已选 (Selected)
                          </span>
                      )}
                    </div>
                  </button>
                )})}

                <button
                  onClick={() => handleAnswer(DONT_KNOW_VALUE)}
                  className={`w-full text-left p-4 border rounded-lg transition-colors ${
                    selectedAnswer === DONT_KNOW_VALUE
                      ? 'border-orange-500 bg-orange-50 shadow-md'
                      : 'border-gray-300 hover:border-orange-400 hover:bg-orange-25'
                  }`}
                >
                  <span className="font-medium mr-3 text-orange-600">
                      {String.fromCharCode(65 + currentQuestion.options.length)}.
                  </span>
                  <span className="text-gray-800">不知道 (Don't know)</span>
                </button>
              </div>
            </div>
          )}

          {checkpointError && (
              <div className="mt-6 mb-4 p-4 bg-red-50 text-red-600 rounded-lg border border-red-200">
                  {checkpointError}
              </div>
          )}
          <div className="flex justify-between">
            <button
              onClick={handlePrevious}
              disabled={currentTest.currentQuestionIndex === 0 || isCheckpointSaving}
              className="flex items-center px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Previous
            </button>

            <button
              onClick={handleNext}
              disabled={isCheckpointSaving}
              className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCheckpointSaving ? 'Saving...' : currentTest.currentQuestionIndex === currentTest.questions.length - 1 ? 'Complete Test' : 'Next'}
              <ChevronRight className="w-4 h-4 ml-2" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReadingTest;
